import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from "@mui/material";
import YmmAutocompleteField from "../../YmmAutocompleteField";
import VerificationLayout from "../../../../layout/VerificationLayout.component";
import { ArtifactStyle } from "./VerficationArtifactsStyles";
import { CheckCircle } from "@mui/icons-material";
import Theme from "../../../../../utils/Theme";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import AppButton from "../../../../base/AppButton.component";
import ArtifactButton from "../../../../base/dialog-body/ArtifactButton.component";
import { useMutation, useQuery } from "@apollo/client";
import { SUBMIT_SERVICE_FOLLOWUP } from "../../../../../graphql/mutation/mutation";
import { useParams } from "react-router-dom";
import {
  FETCH_SERVICE_DETAILS,
  FETCH_YMMT,
} from "../../../../../graphql/query/query";
import ContentShimmer from "react-content-shimmer";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment-timezone";

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const weekDays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const getDayIndex = (dayName) => {
  return weekDays.indexOf(dayName);
};

// Calendar color scheme: lighter green fill with dark text for the
// selected date (for contrast), and the theme's primary green for all
// other calendar letters/numbers — day numbers, weekday captions, the
// month/year header label, toolbar text, and icons (edit pencil,
// prev/next arrows, year-view caret).
const CALENDAR_SELECTED_BG = "#CFF3DC"; // light green
const CALENDAR_SELECTED_BG_HOVER = "#BEEDD0"; // slightly deeper on hover/focus
const CALENDAR_DARK_TEXT = "#14432B"; // dark green-black, kept on the
// selected day specifically — Theme.palette.primary.main text on the
// CALENDAR_SELECTED_BG light-green fill would be too low contrast,
// undoing the readability fix.

// Shared sx applied to the calendar's Paper (both the mobile Dialog and
// the desktop Popper use this) so the color scheme stays consistent
// regardless of which variant MUI renders.
const calendarColorSx = {
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: CALENDAR_SELECTED_BG,
    color: CALENDAR_DARK_TEXT,
    fontWeight: 600,
    "&:hover, &:focus": {
      backgroundColor: CALENDAR_SELECTED_BG_HOVER,
    },
  },
  "& .MuiPickersDay-root": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersDay-today": {
    borderColor: Theme.palette.primary.main,
  },
  "& .MuiPickersCalendarHeader-label": {
    color: Theme.palette.primary.main,
  },
  "& .MuiTypography-caption": {
    color: Theme.palette.primary.main,
  },
  // Mobile Dialog toolbar — "SELECT DATE" label and the large date title.
  "& .MuiPickersToolbar-root .MuiTypography-root": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersToolbarText-root": {
    color: Theme.palette.primary.main,
  },
  // Edit pencil icon in the toolbar.
  "& .MuiPickersToolbar-root .MuiIconButton-root": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersToolbar-root .MuiIconButton-root .MuiSvgIcon-root": {
    color: Theme.palette.primary.main,
  },
  // Prev / next month chevrons. MUI's own .Mui-disabled opacity still
  // applies on top of this, so a disabled chevron stays greyed-out.
  "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersArrowSwitcher-root .MuiSvgIcon-root": {
    color: Theme.palette.primary.main,
  },
  // Dropdown caret next to "August 2026" that opens the year-list view.
  "& .MuiPickersCalendarHeader-switchViewButton": {
    color: Theme.palette.primary.main,
  },
  "& .MuiPickersCalendarHeader-switchViewIcon": {
    color: Theme.palette.primary.main,
  },
  // Year-list view. Unselected years: primary green text. Selected
  // year: same light-fill/dark-text contrast treatment as a selected day.
  "& .PrivatePickersYear-yearButton": {
    color: Theme.palette.primary.main,
  },
  "& .PrivatePickersYear-yearButton.Mui-selected": {
    backgroundColor: CALENDAR_SELECTED_BG,
    color: CALENDAR_DARK_TEXT,
    fontWeight: 600,
  },
  // Fallback selectors for @mui/x-date-pickers v5 patch versions that use
  // MuiYearPicker-root/button instead of the PrivatePickersYear- classes.
  "& .MuiYearPicker-root button": {
    color: Theme.palette.primary.main,
  },
  "& .MuiYearPicker-root button.Mui-selected": {
    backgroundColor: CALENDAR_SELECTED_BG,
    color: CALENDAR_DARK_TEXT,
    fontWeight: 600,
  },
};

const selectorSx = {
  backgroundColor: "white",
  borderRadius: "6px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#00AB55" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#00AB55" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00AB55" },
};

// ── Touch-friendly sizing for the Time / Preference selects and the ──
// Date input (mobile only). 56px meets the common 48px+ touch-target
// guidance and is easier for users with limited dexterity. Desktop is
// left unchanged. Mirrors AppointmentResponse.jsx.
const touchSelectSx = (isMobile) =>
  isMobile
    ? {
        "& .MuiSelect-select.MuiSelect-select": {
          minHeight: "56px",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          fontSize: "18px",
          paddingRight: "48px",
        },
        "& .MuiSelect-icon": { fontSize: "2.2rem", right: "10px" },
      }
    : {};

const touchDateInputSx = (isMobile) =>
  isMobile
    ? {
        "& .MuiInputBase-root": { minHeight: "56px" },
        "& .MuiInputBase-input": {
          height: "56px",
          boxSizing: "border-box",
          fontSize: "18px",
          padding: "0 14px",
        },
        "& .MuiInputAdornment-root .MuiSvgIcon-root": { fontSize: "2.2rem" },
      }
    : {};

const touchMenuItemSx = (isMobile) =>
  isMobile
    ? {
        minHeight: "56px",
        fontSize: "18px",
        whiteSpace: "normal",
        margin: "6px",
      }
    : {};

// Compact variants — used for the New Vehicle 3-column row (Date / Time /
// Preference side by side), where the boxes need to stay their current
// (narrower) size, but the value text should still be the larger, more
// legible size.
const touchSelectSxCompact = (isMobile) =>
  isMobile
    ? {
        "& .MuiSelect-select.MuiSelect-select": {
          fontSize: "16px",
        },
      }
    : {};

const touchDateInputSxCompact = (isMobile) =>
  isMobile
    ? {
        "& .MuiInputBase-input": {
          fontSize: "16px",
        },
      }
    : {};

const formFieldWrapperSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
};

const schedulingFormSx = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: "16px", sm: "20px" },
  alignItems: "stretch",
  width: "100%",
  maxWidth: { xs: "100%", sm: "280px" }, 
  margin: "0 auto",
  px: { xs: 1, sm: 0 },
};

const newVehicleSchedulingFormSx = {
  ...schedulingFormSx,
  maxWidth: { xs: "100%", sm: "640px" },
  gap: { xs: "14px", sm: "16px" },
};

const newVehicleRowSx = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "flex-start",
  gap: { xs: "8px", sm: "12px" },
  width: "100%",
};

const newVehicleThreeColFieldSx = {
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
};

const ymmAutocompletePaperSx = {
  minWidth: { xs: 125, sm: "auto" },
};

const avatarOverlayContainerSx = {
  position: "absolute",
  bottom: { xs: 48, sm: 70 },
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 3,
  width: { xs: "75%", sm: "60%", md: "60%" }, 
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  transition: "opacity 0.6s ease, transform 0.6s ease",
};

const avatarOverlayBoxSx = {
  width: "100%", 
  bgcolor: "white",
  border: "2px solid grey",
  borderRadius: "5px",
  p: "5px",
  maxHeight: { xs: "36vh", sm: "40vh" },
  overflowY: "auto",
};

const overlaySchedulingFormSx = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: "8px", sm: "10px" },
  alignItems: "stretch",
  width: "100%",
};

const overlayNewVehicleSchedulingFormSx = {
  ...overlaySchedulingFormSx,
  gap: { xs: "6px", sm: "8px" },
};

const overlayNewVehicleRowSx = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "flex-start",
  gap: { xs: "4px", sm: "6px" },
  width: "100%",
};

const SubmitVehicle = () => {
  const params = useParams();
  const videoRef = useRef(null);
  console.log("ALL PARAMS:", params);

  const followupNumber = params?.followupNumber || null;
  const isNewVehicle = params?.isNewVehicle === "true";

  const [success, setSuccess] = useState(false);
  const [alreadyBooked, setAlreadyBooked] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState("");
  const [appointmentPreference, setAppointmentPreference] = useState("");
  const [openingHours, setOpeningHours] = useState([]);
  const [vehicleDetail, setVehicleDetail] = useState({
    year: "",
    make: "",
    model: "",
  });
  const [saveYear, setSaveYear] = useState([]);
  const [saveMake, setSaveMake] = useState([]);
  const [saveModel, setSaveModel] = useState([]);

  // ── Calendar "fill the 9:16 layout" sizing + color scheme ───────────
  // On touch devices, @mui/x-date-pickers v5 renders DatePicker as a
  // MobileDatePicker internally (Dialog, not Popper). We measure the
  // calendar's natural (1x) size once, then compute the largest scale
  // factor that still fits inside the real available viewport,
  // recomputed on resize/rotation so it always fills as much of the
  // frame as possible. See renderDateField below for where this is used.
  const isMobile = useMediaQuery("(max-width:750px)");
  const calendarPaperRef = useRef(null);
  const calendarNaturalSize = useRef(null); // cached {width, height} at 1x
  const [calendarScale, setCalendarScale] = useState(1);

  const computeCalendarScale = useCallback(() => {
    if (!isMobile) return;
    const paperEl = calendarPaperRef.current;
    if (!paperEl) return;

    if (!calendarNaturalSize.current) {
      const rect = paperEl.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return; // not painted yet
      calendarNaturalSize.current = {
        width: rect.width / (calendarScale || 1),
        height: rect.height / (calendarScale || 1),
      };
    }
    const { width: naturalWidth, height: naturalHeight } =
      calendarNaturalSize.current;

    const vv = window.visualViewport;
    const viewportWidth = vv ? vv.width : window.innerWidth;
    const viewportHeight = vv ? vv.height : window.innerHeight;

    const MARGIN = 0.92; // small gutter so it never touches screen edges
    const scaleX = (viewportWidth * MARGIN) / naturalWidth;
    const scaleY = (viewportHeight * MARGIN) / naturalHeight;

    const nextScale = Math.min(scaleX, scaleY);
    setCalendarScale(Math.min(Math.max(nextScale, 1), 2.4));
  }, [isMobile, calendarScale]);

  useEffect(() => {
    if (!isMobile) return;
    window.visualViewport?.addEventListener("resize", computeCalendarScale);
    window.addEventListener("orientationchange", computeCalendarScale);
    return () => {
      window.visualViewport?.removeEventListener(
        "resize",
        computeCalendarScale,
      );
      window.removeEventListener("orientationchange", computeCalendarScale);
    };
  }, [isMobile, computeCalendarScale]);

  // Toggle states
  const [isSpanish, setIsSpanish] = useState(false);
  const [isSpokeperson, setIsSpokeperson] = useState(true);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);

  // Video states
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [videoKey, setVideoKey] = useState(0);
  const [animateIn, setAnimateIn] = useState(false);
  const [duration, setDuration] = useState(0);
  const [showSurveyButton, setShowSurveyButton] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setIsLoading] = useState(true);
  const revealSchedulingForm = () => {
    setShowSurveyButton(true);
    setIsLoading(false);
  };

  const handleVideoProgress = (video) => {
    if (!video) return;
    const total = video.duration || 0;
    if (total > 0 && (video.ended || video.currentTime >= total - 1)) {
      revealSchedulingForm();
    }
  };

  const { data: serviceData, loading: serviceLoading } = useQuery(
    FETCH_SERVICE_DETAILS,
    {
      variables: {
        userId: parseInt(params?.userId),
        employeeUserId: parseInt(params?.employeeId),
        dealershipId: parseInt(params?.dealershipId),
        vehicleId: parseInt(params?.vehicleId),
        followupNumber: parseInt(params?.followupNumber),
      },
      skip: !followupNumber,
    },
  );

  const { data: yearData } = useQuery(FETCH_YMMT, {
    variables: { ymmtInput: { year: "", make: "", model: "" } },
    skip: !isNewVehicle,
  });

  const { data: fetchYmmtOutput, refetch: refetchYmmt } = useQuery(FETCH_YMMT, {
    variables: {
      ymmtInput: {
        year: vehicleDetail?.year ?? "",
        make: vehicleDetail?.make ?? "",
        model: vehicleDetail?.model ?? "",
      },
    },
    skip: !isNewVehicle,
  });

  // Dealer's own brand — pinned as the first option in the new-vehicle
  // Make dropdown, mirroring the same decision made in
  // VehicleTransactionResponse.jsx. Prefers the brand the dealer actually
  // sells (e.g. "TOYOTA") so it matches what a customer booking a
  // new-vehicle service follow-up is most likely to pick. Requires
  // ServiceDetails.brand_name to be added to the schema / the
  // FETCH_SERVICE_DETAILS selection on the backend — falls back to no
  // pinned option until then.
  const dealerBrandName = serviceData?.fetchServiceDetails?.brand_name;
  const dealerName = dealerBrandName ? dealerBrandName.toUpperCase() : "";

  const makeOptionsWithDealer = useMemo(() => {
    const base = saveMake || [];
    if (!dealerName) return base;
    const rest = base.filter(
      (m) => m?.toLowerCase() !== dealerName.toLowerCase(),
    );
    return [dealerName, ...rest];
  }, [saveMake, dealerName]);

  // Set language based on user_language_id from API
  useEffect(() => {
    const userLanguageId = serviceData?.fetchServiceDetails?.user_language_id;
    if (userLanguageId === 2) {
      setIsSpanish(true);
    } else if (userLanguageId === 1) {
      setIsSpanish(false);
    }
  }, [serviceData]);

  // Pick english_url or spanish_url based on current language toggle
  useEffect(() => {
    if (!isSpokeperson) {
      setSelectedUrl(null);
      setShowSurveyButton(false);
      return;
    }
    const videos = serviceData?.fetchServiceDetails?.service_ai_videos;
    if (!videos) {
      setSelectedUrl(null);
      return;
    }
    const url = isSpanish
      ? videos.spanish_url || videos.selected_url || null
      : videos.english_url || videos.selected_url || null;
    setSelectedUrl(url || null);
    setVideoKey((prev) => prev + 1);
    setShowSurveyButton(false);
    setAnimateIn(false);
    setIsLoading(true);
  }, [isSpokeperson, isSpanish, serviceData]);

  // Video lifecycle - similar to original
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selectedUrl) return;

    const handleLoadedMetadata = () => {
      setIsLoading(false);
      setDuration(video.duration);
    };

    const handleCanPlay = () => {
      setAnimateIn(true);
      video.play().catch(() => {});
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("canplay", handleCanPlay);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [selectedUrl, videoKey]);

  // Show scheduling form near end of video or when playback completes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const revealForm = () => {
      revealSchedulingForm();
    };

    const handleTimeUpdate = () => {
      handleVideoProgress(video);
    };

    const handleEnded = () => {
      revealForm();
    };

    const handleSeeked = () => {
      handleVideoProgress(video);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("seeked", handleSeeked);
    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("seeked", handleSeeked);
    };
  }, [duration, selectedUrl, videoKey]);

  // Pause/reset when spokesperson turned off
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!isSpokeperson) {
      try {
        video.pause();
        video.currentTime = 0;
        video.removeAttribute("src");
        video.load();
      } catch (_) {}
      setAnimateIn(false);
      setShowSurveyButton(false);
    }
  }, [isSpokeperson]);

  useEffect(() => {
    if (serviceData?.fetchServiceDetails?.openingHours) {
      setOpeningHours(serviceData.fetchServiceDetails.openingHours);
    }
  }, [serviceData]);

  useEffect(() => {
    if (!isNewVehicle) return;
    const vehicle = serviceData?.fetchServiceDetails?.vehicle;
    if (vehicle?.year || vehicle?.make || vehicle?.model) {
      setVehicleDetail({
        year: vehicle.year ? String(vehicle.year) : "",
        make: vehicle.make || "",
        model: vehicle.model || "",
      });
    }
  }, [serviceData, isNewVehicle]);

  useEffect(() => {
    if (!isNewVehicle || !yearData?.fetchYmmt?.yearArray) return;
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    const years = yearData.fetchYmmt.yearArray
      .map(Number)
      .filter((y) => y !== 9999 && y <= nextYear)
      .sort((a, b) => b - a);
    setSaveYear(years.map(String));
  }, [yearData, isNewVehicle]);

  useEffect(() => {
    if (!isNewVehicle || !fetchYmmtOutput?.fetchYmmt) return;
    const { makeArray, modelArray } = fetchYmmtOutput.fetchYmmt;
    if (
      vehicleDetail?.year &&
      vehicleDetail?.make === "" &&
      vehicleDetail?.model === ""
    ) {
      setSaveMake(makeArray || []);
      setSaveModel([]);
    } else if (
      vehicleDetail?.year &&
      vehicleDetail?.make &&
      vehicleDetail?.model === ""
    ) {
      setSaveModel(modelArray || []);
    }
  }, [fetchYmmtOutput, vehicleDetail, isNewVehicle]);

  const generateTimeSlots = () => {
    if (!appointmentDate) return [];

    const dayOfWeek = new Date(appointmentDate).getDay();
    const dayData = openingHours?.find((d) => getDayIndex(d.day) === dayOfWeek);

    if (dayData && !dayData.isclosed) {
      const openTime = moment(dayData.opens_at, "HH:mm");
      const closeTime = moment(dayData.closes_at, "HH:mm");
      const slots = [];
      while (openTime.isBefore(closeTime)) {
        slots.push(openTime.format("hh:mm A"));
        openTime.add(30, "minutes");
      }
      return slots;
    }
    return [];
  };

  const getPreferenceOptions = () => {
    const userLanguage = isSpanish ? "es" : "en";

    try {
      let options = null;

      if (userLanguage === "es") {
        options = serviceData?.fetchServiceDetails?.es_preference_options;
      } else {
        options = serviceData?.fetchServiceDetails?.preference_options;
      }

      if (!options) return [];

      if (Array.isArray(options)) return options;

      if (typeof options === "string") {
        try {
          const parsed = JSON.parse(options);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          if (options.includes(",")) {
            return options.split(",").map((opt) => opt.trim());
          }
        }
      }

      return [];
    } catch (error) {
      console.error("Error parsing preference options:", error);
      return [];
    }
  };

  // Dynamic message fetched from API based on followupNumber
  const getUIMessage = () => {
    if (!followupNumber || !serviceData?.fetchServiceDetails) return "";

    const details = serviceData.fetchServiceDetails;
    const messageKey = isSpanish
      ? `es_followup_message_${followupNumber}`
      : `followup_message_${followupNumber}`;

    return details[messageKey] || "";
  };

  const getDateLabel = () => (isSpanish ? "Seleccionar fecha" : "Select Date");
  const getTimeLabel = () => (isSpanish ? "Seleccionar hora" : "Select Time");
  const getPreferenceLabel = () =>
    isSpanish ? "Seleccionar preferencia" : "Select Preference";
  const getSelectDateFirstText = () =>
    isSpanish ? "Seleccionar fecha primero" : "Select Date First";
  const getStoreClosedText = () =>
    isSpanish ? "Tienda cerrada" : "Store Closed";
  const getSelectTimeText = () =>
    isSpanish ? "Seleccionar hora" : "Select Time";
  const getSubmitText = () => (isSpanish ? "Entregar" : "Submit");
  const getSuccessText = () => (isSpanish ? "ÉXITO" : "SUCCESS");
  const getCloseText = () => (isSpanish ? "Cerrar" : "Close");
  const getAlreadyBookedText = () =>
    isSpanish
      ? "Ya ha reservado una cita"
      : "You have already booked an appointment";
  const isYmmComplete = () =>
    Boolean(vehicleDetail.year && vehicleDetail.make);
  const isSubmitReady = () => {
    if (isNewVehicle) {
      return isYmmComplete();
    }
    return Boolean(appointmentDate && appointmentTime);
  };

  const [submitServiceLink, { loading: serviceLoading2 }] = useMutation(
    SUBMIT_SERVICE_FOLLOWUP,
  );

  const submitServiceHandler = () => {
    if (isNewVehicle && !isYmmComplete()) {
      alert(
        isSpanish
          ? "Por favor seleccione año y marca de su vehículo"
          : "Please select year and make for your vehicle",
      );
      return;
    }

    if (!isNewVehicle && (!appointmentDate || !appointmentTime)) {
      alert(
        isSpanish
          ? "Por favor seleccione fecha y hora para su cita"
          : "Please select date and time for your appointment",
      );
      return;
    }

    const formattedDate = appointmentDate
      ? moment.tz(appointmentDate, userTimezone).format("MM/DD/YYYY")
      : null;
    const parsedVehicleId = parseInt(params.vehicleId, 10);

    submitServiceLink({
      variables: {
        serviceFollowupInput: {
          user_id: parseInt(params.userId, 10),
          employee_user_id: parseInt(params.employeeId, 10),
          dealership_id: parseInt(params.dealershipId, 10),
          followup_number: parseInt(followupNumber || 1, 10),
          vehicle_id: Number.isNaN(parsedVehicleId) ? null : parsedVehicleId,
          new_vehicle: isNewVehicle,
          vehicle_status_id: isNewVehicle ? 1 : null,
          year: isNewVehicle ? vehicleDetail.year : null,
          make: isNewVehicle ? vehicleDetail.make : null,
          model: isNewVehicle ? vehicleDetail.model || null : null,
          appointment_date: formattedDate,
          appointment_time: appointmentTime || null,
          appointment_preference: appointmentPreference || null,
          timezone: userTimezone,
        },
      },
    })
      .then((res) => {
        console.log("RESPONSE:", res?.data);
        const result = res?.data?.submitServiceFollowup;

        // Customer already has an appointment booked for this vehicle
        if (result?.alreadyBooked) {
          setAlreadyBooked(true);
          return;
        }

        if (result?.status) {
          setSuccess(true);
          setSubmitted(true);
          setTimeout(() => {
            setSuccess(false);
            setSubmitted(false);
            setAppointmentDate(null);
            setAppointmentTime("");
            setAppointmentPreference("");
            setSelectedAnswer(null);
            if (isNewVehicle) {
              setVehicleDetail({ year: "", make: "", model: "" });
            }
          }, 3000);
        }
      })
      .catch((err) => {
        console.error("Error submitting service follow-up:", err);
        alert(
          isSpanish
            ? "Hubo un error al programar su cita. Por favor intente de nuevo."
            : "There was an error scheduling your appointment. Please try again.",
        );
      });
  };

  const getOpeningHoursForDate = () => {
    if (!appointmentDate) return null;
    const dayOfWeek = new Date(appointmentDate).getDay();
    return openingHours?.find((d) => getDayIndex(d.day) === dayOfWeek);
  };

  const isDateDisabled = (date) => {
    const dayOfWeek = date.getDay();
    const dayData = openingHours?.find((d) => getDayIndex(d.day) === dayOfWeek);
    return dayData?.isclosed;
  };

  useEffect(() => {
    setAppointmentTime("");
  }, [appointmentDate]);

  const dealershipLogo = serviceData?.fetchServiceDetails?.dealership_logo;

  // Toggle component (Language and Mode dropdowns)
  const Toggle = () => {
    const dropdownButtonStyles = {
      padding: "13px 7px",
      fontSize: window.innerWidth <= 750 ? "10px" : "12px",
      cursor: "pointer",
      color: Theme.palette.primary.main,
      fontWeight: 500,
      width: window.innerWidth <= 750 ? "70px" : "80px",
      height: window.innerWidth <= 750 ? "32px" : "36px",
      display: "flex",
      alignItems: "center",
      position: "relative",
      justifyContent: "space-between",
    };

    const dropdownContentStyles = {
      position: "absolute",
      top: "100%",
      left: "0%",
      backgroundColor: "#fff",
      width: {
        xs: "100%",
        sm: "70%",
        md: "60%",
        lg: "65%",
      },
      boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
      zIndex: 1000,
      borderRadius: "4px",
      border: "1px solid #ddd",
      marginTop: "4px",
      overflow: "hidden",
    };

    const dropdownItemStyles = {
      padding: "10px 12px",
      cursor: "pointer",
      fontSize: window.innerWidth <= 750 ? "10px" : "12px",
      backgroundColor: "#fff",
      "&:hover": { backgroundColor: "#f5f5f5" },
    };

    const selectedItemStyles = {
      ...dropdownItemStyles,
      backgroundColor: "#defae5ff",
      fontWeight: 600,
      color: Theme.palette.primary.main,
    };

    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", position: "relative" }}
      >
        {/* Language Dropdown */}
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={() => {
              setLanguageOpen(!languageOpen);
              setModeOpen(false);
            }}
            sx={dropdownButtonStyles}
          >
            <span style={{ flex: 1, textAlign: "start" }}>
              {isSpanish ? "SPANISH" : "ENGLISH"}
            </span>
            <span
              style={{ fontSize: "12px", color: Theme.palette.primary.main }}
            >
              ▼
            </span>
          </Box>
          {languageOpen && (
            <Box sx={dropdownContentStyles}>
              <Box
                onClick={() => {
                  setIsSpanish(false);
                  setLanguageOpen(false);
                }}
                sx={!isSpanish ? selectedItemStyles : dropdownItemStyles}
              >
                ENGLISH
              </Box>
              <Box
                onClick={() => {
                  setIsSpanish(true);
                  setLanguageOpen(false);
                }}
                sx={isSpanish ? selectedItemStyles : dropdownItemStyles}
              >
                SPANISH
              </Box>
            </Box>
          )}
        </Box>

        {/* Mode Dropdown */}
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={() => {
              setModeOpen(!modeOpen);
              setLanguageOpen(false);
            }}
            sx={{
              ...dropdownButtonStyles,
              opacity: serviceData?.fetchServiceDetails?.service_ai_videos
                ? 1
                : 0.5,
              pointerEvents: serviceData?.fetchServiceDetails?.service_ai_videos
                ? "auto"
                : "none",
            }}
          >
            <span style={{ flex: 1, textAlign: "start" }}>
              {isSpokeperson ? "AVATAR" : "READ"}
            </span>
            <span
              style={{ fontSize: "12px", color: Theme.palette.primary.main }}
            >
              ▼
            </span>
          </Box>
          {modeOpen && (
            <Box sx={dropdownContentStyles}>
              <Box
                onClick={() => {
                  setIsSpokeperson(true);
                  setModeOpen(false);
                }}
                sx={isSpokeperson ? selectedItemStyles : dropdownItemStyles}
              >
                AVATAR
              </Box>
              <Box
                onClick={() => {
                  setIsSpokeperson(false);
                  setModeOpen(false);
                }}
                sx={!isSpokeperson ? selectedItemStyles : dropdownItemStyles}
              >
                READ
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Handle close
  const handleClose = () => {
    window.history.back();
  };

  const renderYmmFields = (fieldSx = formFieldWrapperSx) => (
    <>
      <Box sx={fieldSx}>
        <YmmAutocompleteField
          fullWidth
          size="small"
          fieldType="year"
          options={saveYear || []}
          value={vehicleDetail.year || ""}
          label={isSpanish ? "Año" : "Year"}
          isSpanish={isSpanish}
          textFieldSx={selectorSx}
          paperSx={ymmAutocompletePaperSx}
          onChange={(newValue) => {
            setSaveMake([]);
            setSaveModel([]);
            setVehicleDetail({ year: newValue, make: "", model: "" });
          }}
          onRefetch={(newValue) =>
            refetchYmmt({
              ymmtInput: { year: newValue, make: "", model: "" },
            })
          }
        />
      </Box>

      <Box sx={fieldSx}>
        <YmmAutocompleteField
          fullWidth
          size="small"
          fieldType="make"
          options={makeOptionsWithDealer}
          value={vehicleDetail.make || ""}
          label={isSpanish ? "Marca" : "Make"}
          isSpanish={isSpanish}
          disabled={!vehicleDetail.year}
          textFieldSx={selectorSx}
          paperSx={ymmAutocompletePaperSx}
          onChange={(newValue) => {
            setSaveModel([]);
            setVehicleDetail({
              ...vehicleDetail,
              make: newValue,
              model: "",
            });
          }}
          onRefetch={(newValue) =>
            refetchYmmt({
              ymmtInput: {
                year: vehicleDetail.year,
                make: newValue,
                model: "",
              },
            })
          }
        />
      </Box>

      <Box sx={fieldSx}>
        <YmmAutocompleteField
          fullWidth
          size="small"
          fieldType="model"
          options={saveModel || []}
          value={vehicleDetail.model || ""}
          label={isSpanish ? "Modelo" : "Model"}
          isSpanish={isSpanish}
          disabled={!vehicleDetail.make || !vehicleDetail.year}
          textFieldSx={selectorSx}
          paperSx={ymmAutocompletePaperSx}
          onChange={(newValue) => {
            setVehicleDetail({
              ...vehicleDetail,
              model: newValue,
            });
          }}
        />
      </Box>
    </>
  );

  const renderDateField = (isClosed, fieldSx = formFieldWrapperSx, compact = false) => (
    <Box sx={fieldSx}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          value={appointmentDate}
          minDate={new Date()}
          shouldDisableDate={isDateDisabled}
          // v5 picks Desktop (Popper) vs Mobile (Dialog) based on POINTER
          // TYPE (coarse/touch => Dialog). We keep that native behavior on
          // purpose: a centered Dialog can scale up to fill the 9:16 frame
          // without overflowing, the way an anchored Popper dropdown would.
          onOpen={() => {
            // Measure/scale after the Dialog has actually painted.
            // Double rAF: first waits for React's commit, second for the
            // browser's layout/paint of that commit.
            requestAnimationFrame(() => {
              requestAnimationFrame(computeCalendarScale);
            });
          }}
          onChange={(val) => setAppointmentDate(val)}
          // Scale the MOBILE DIALOG's Paper to fill as much of the 9:16
          // frame as possible. transform:scale multiplies every internal
          // MUI element (day cells, header, row heights) uniformly,
          // sidestepping v5's mix of fixed-px inline styles that
          // per-class overrides can't reliably reach. Color scheme
          // (calendarColorSx) is merged in alongside it.
          DialogProps={
            isMobile
              ? {
                  PaperProps: {
                    ref: calendarPaperRef,
                    sx: {
                      transform: `scale(${calendarScale})`,
                      transformOrigin: "center center",
                      transition: "transform 0.15s ease-out",
                      ...calendarColorSx,
                    },
                  },
                }
              : undefined
          }
          // Desktop (Popper) variant — same color scheme, no scaling
          // (scaling is a mobile-only "fill the 9:16 frame" concern).
          PaperProps={
            !isMobile
              ? {
                  sx: calendarColorSx,
                }
              : undefined
          }
          renderInput={(params) => (
            <TextField
              size="small"
              {...params}
              fullWidth
              placeholder={getDateLabel()}
              error={isClosed}
              helperText={isClosed ? getStoreClosedText() : ""}
              // Box stays its current size in the New Vehicle row
              // (compact); elsewhere the input box is enlarged on
              // mobile. The calendar popup itself is untouched either way.
              sx={{
                ...selectorSx,
                ...(compact
                  ? touchDateInputSxCompact(isMobile)
                  : touchDateInputSx(isMobile)),
              }}
            />
          )}
        />
      </LocalizationProvider>
    </Box>
  );

  const renderTimeField = (timeSlots, fieldSx = formFieldWrapperSx, compact = false) => (
    <Box sx={fieldSx}>
      <FormControl fullWidth size="small">
        <Select
          placeholder={getTimeLabel()}
          value={appointmentTime || ""}
          displayEmpty
          renderValue={(selected) => {
            if (selected === "") {
              return (
                <span style={{ color: "#9e9e9e" }}>{getTimeLabel()}</span>
              );
            }
            return selected;
          }}
          MenuProps={{
            PaperProps: { style: { maxHeight: isMobile ? "60vh" : 200 } },
            anchorOrigin:
              window.innerWidth <= 750
                ? { vertical: "top", horizontal: "left" }
                : { vertical: "bottom", horizontal: "left" },
            transformOrigin:
              window.innerWidth <= 750
                ? { vertical: "bottom", horizontal: "left" }
                : "auto",
          }}
          onChange={(e) => setAppointmentTime(e.target.value)}
          sx={{
            ...selectorSx,
            ...(compact ? touchSelectSxCompact(isMobile) : touchSelectSx(isMobile)),
          }}
        >
          {timeSlots.map((slot, index) => (
            <MenuItem
              key={index}
              value={slot}
              dense={!isMobile}
              sx={{
                border: "1px solid #c0c0c0",
                padding: window.innerWidth <= 750 ? 0 : "8px",
                borderRadius: "4px",
                justifyContent: "center",
                alignItems: "center",
                margin: window.innerWidth <= 750 ? "6px" : "4px",
                fontSize: isMobile ? "18px" : "16px",
                ...(isMobile && { minHeight: "56px" }),
              }}
            >
              {slot}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const renderPreferenceField = (
    preferenceOptions,
    fieldSx = formFieldWrapperSx,
    compact = false,
  ) => (
    <Box sx={fieldSx}>
      <FormControl fullWidth size="small">
        <Select
          sx={{
            ...selectorSx,
            ...(compact ? touchSelectSxCompact(isMobile) : touchSelectSx(isMobile)),
          }}
          value={appointmentPreference || ""}
          onChange={(e) => setAppointmentPreference(e.target.value)}
          size="small"
          disabled={!appointmentDate}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span style={{ color: "#9e9e9e" }}>
                  {getPreferenceLabel()}
                </span>
              );
            }
            return selected;
          }}
        >
          {preferenceOptions?.map((option, index) => (
            <MenuItem key={index} value={option} sx={touchMenuItemSx(isMobile)}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  // Render appointment selectors (reused for both AI and read mode)
  const renderAppointmentSelectors = (variant = "default") => {
    const isOverlay = variant === "overlay";
    const currentOpeningHours = getOpeningHoursForDate();
    const isClosed = currentOpeningHours?.isclosed;
    const timeSlots = generateTimeSlots();
    const preferenceOptions = getPreferenceOptions();

    if (isNewVehicle) {
      return (
        <Box sx={isOverlay ? overlayNewVehicleSchedulingFormSx : newVehicleSchedulingFormSx}>
          <Box sx={isOverlay ? overlayNewVehicleRowSx : newVehicleRowSx}>
            {renderYmmFields(newVehicleThreeColFieldSx)}
          </Box>

          <Box sx={isOverlay ? overlayNewVehicleRowSx : newVehicleRowSx}>
            {/* compact: keep these three boxes their current (narrower)
                size in the 3-column row; only the standalone fields below
                (renderAppointmentSelectors' non-new-vehicle branch) grow
                to the larger touch-target size. */}
            {renderDateField(isClosed, newVehicleThreeColFieldSx, true)}
            {renderTimeField(timeSlots, newVehicleThreeColFieldSx, true)}
            {renderPreferenceField(preferenceOptions, newVehicleThreeColFieldSx, true)}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={isOverlay ? overlaySchedulingFormSx : schedulingFormSx}>
        {renderDateField(isClosed)}
        {renderTimeField(timeSlots)}
        {renderPreferenceField(preferenceOptions)}
      </Box>
    );
  };

  const hasText = getUIMessage() && String(getUIMessage()).trim().length > 0;
  const showScheduling = Boolean(followupNumber && serviceData?.fetchServiceDetails);

  if (serviceLoading) {
    return (
      <VerificationLayout>
        <Box sx={{ p: 3 }}>
          <ContentShimmer />
        </Box>
      </VerificationLayout>
    );
  }

  return (
    <VerificationLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "10px",
          height: "100%",
          position: "relative",
        }}
      >
        {/* HEADER ROW: [Toggle | Logo | Close] */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          {/* LEFT: Language + Mode dropdowns */}
          <Box sx={{ width: "30%" }}>
            <Toggle />
          </Box>

          {/* CENTER: Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {dealershipLogo ? (
              <img
                src={dealershipLogo}
                width={150}
                height={60}
                alt="dealership logo"
              />
            ) : (
              <ContentShimmer />
            )}
          </Box>

          {/* RIGHT: Close button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              width: "30%",
            }}
          >
            <AppButton
              buttonText={getCloseText()}
              variant={"text"}
              sx={{
                color: Theme.palette.error.main,
                fontSize: window.innerWidth <= 750 ? "12px" : "16px",
              }}
              clickHandler={handleClose}
            />
          </Box>
        </Box>

        {/* BODY - Avatar or Read Mode */}
        {isSpokeperson && selectedUrl ? (
          // ─── AVATAR MODE ───────────────────────────────────────────────
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
            }}
          >
            {/* Loading indicator */}
            {loading && selectedUrl && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  bgcolor: "#fff",
                }}
              >
                <CircularProgress color="primary" />
              </Box>
            )}

            <Box
              component="video"
              ref={videoRef}
              key={videoKey}
              controls
              preload="metadata"
              onEnded={revealSchedulingForm}
              onTimeUpdate={(e) => handleVideoProgress(e.currentTarget)}
              onSeeked={(e) => handleVideoProgress(e.currentTarget)}
              sx={{
                height: { xs: "74vh", sm: "100vh", md: "80vh" },
                maxHeight: { xs: "520px", sm: "750px", md: "497px" },
                width: { xs: "90%", sm: "90%", md: "50%" },
                maxWidth: { xs: "280px", sm: "400px", md: "250px" },
                objectFit: "cover",
                objectPosition: "center",
                transition: "opacity 0.4s ease-in-out",
                opacity: animateIn ? 1 : 0,
                backgroundColor: "#fff",
                borderRadius: "12px",
              }}
            >
              <source src={selectedUrl} type="video/mp4" />
            </Box>

            {/* Scheduling overlay at end of video — bottom of container, face stays visible */}
            {showSurveyButton && showScheduling && (
              <Box sx={avatarOverlayContainerSx}>
                <Box sx={avatarOverlayBoxSx}>
                  {renderAppointmentSelectors("overlay")}
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          // ─── READ MODE ─────────────────────────────────────────────────
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: "8px",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
              overflow: "auto",
              p: 3,
            }}
          >
            {/* Dynamic Message from API */}
            {hasText && (
              <Box sx={{ mt: 2, mb: 1, width: "100%" }}>
                <pre style={{ ...ArtifactStyle.textBox }}>
                  <p>{getUIMessage()}</p>
                </pre>
              </Box>
            )}

            {/* Appointment Section */}
            {showScheduling && (
              <Box id="appointment-section" sx={{ width: "100%", mt: 2 }}>
                {renderAppointmentSelectors()}
              </Box>
            )}
          </Box>
        )}

        {/* SHARED Submit Button - visible once scheduling form is shown in avatar mode */}
        {showScheduling && (!isSpokeperson || !selectedUrl || showSurveyButton) && (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#fff",
              py: 2,
              zIndex: 10,
            }}
          >
            {alreadyBooked ? (
              <Box sx={{ display: "flex", my: "14px" }}>
                <CheckCircle color={"primary"} />
                <span
                  style={{
                    color: Theme.palette.primary.main,
                    marginLeft: "4px",
                  }}
                >
                  {getAlreadyBookedText()}
                </span>
              </Box>
            ) : success ? (
              <Box sx={{ display: "flex", my: "14px" }}>
                <CheckCircle color={"primary"} />
                <span
                  style={{
                    color: Theme.palette.primary.main,
                    marginLeft: "4px",
                  }}
                >
                  {getSuccessText()}
                </span>
              </Box>
            ) : (
              <AppButton
                buttonText={getSubmitText()}
                disabled={!isSubmitReady()}
                clickHandler={submitServiceHandler}
                sx={{ width: "60%", alignSelf: "center" }}
                loading={serviceLoading2}
              />
            )}
          </Box>
        )}
      </Box>
    </VerificationLayout>
  );
};

export default SubmitVehicle;